// @ts-nocheck
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SLOTS = [
  { id: "event_page_top", label: "Above the event video", description: "Public event page — top hero region", email: false, recommendedSize: "1500 × 300 px (5:1 ratio) — JPG or PNG, < 300 KB" },
  { id: "event_page_pre_faq", label: "Above the FAQs", description: "Public event page — just before the FAQ section", email: false, recommendedSize: "1500 × 300 px (5:1 ratio) — JPG or PNG, < 300 KB" },
  { id: "event_page_sidebar", label: "Sidebar, under social share", description: "Public event page — right sidebar", email: false, recommendedSize: "600 × 600 px (1:1 square) — JPG or PNG, < 200 KB" },
  { id: "email_header", label: "Top of system emails", description: "Header banner in confirmation/reminder emails", email: true, recommendedSize: "1200 × 300 px (4:1 ratio) — PNG preferred, < 200 KB" },
  { id: "email_inline", label: "Mid-body of system emails", description: "Inline banner in system emails", email: true, recommendedSize: "1200 × 300 px (4:1 ratio) — PNG preferred, < 200 KB" },
  { id: "email_footer", label: "Bottom of system emails", description: "Footer banner in system emails", email: true, recommendedSize: "1200 × 200 px (6:1 ratio) — PNG preferred, < 150 KB" },
];

interface Props {
  eventId: string | null;
  event?: any;
  updateEvent?: (patch: any) => void;
}

export function BannersSection({ eventId, event, updateEvent }: Props) {
  const queryClient = useQueryClient();
  const [pickerSlot, setPickerSlot] = useState<string | null>(null);

  const { data: placements = [] } = useQuery({
    queryKey: ["event-ad-placements", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ad_placements")
        .select(`
          id, placement_slot, sort_order, is_active,
          asset:ad_assets(id, name, advertiser_name, image_url, alt_text)
        `)
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const assignedAssets = useMemo(() => {
    const out: Record<string, any[]> = {};
    for (const p of placements) {
      if (!p.asset) continue;
      out[p.placement_slot] = out[p.placement_slot] || [];
      out[p.placement_slot].push({
        ...p.asset,
        placement_id: p.id,
        sort_order: p.sort_order,
      });
    }
    return out;
  }, [placements]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["event-ad-placements", eventId] });

  const addAsset = async (slotId: string, assetId: string) => {
    const existingCount = assignedAssets[slotId]?.length || 0;
    const { error } = await (supabase as any).from("ad_placements").insert({
      event_id: eventId,
      asset_id: assetId,
      placement_slot: slotId,
      sort_order: existingCount,
      is_active: true,
    });
    if (error) {
      toast.error("Failed to add banner");
      return;
    }
    toast.success("Banner added");
    refresh();
    setPickerSlot(null);
  };

  const removeAsset = async (placementId: string) => {
    const { error } = await (supabase as any)
      .from("ad_placements")
      .delete()
      .eq("id", placementId);
    if (error) {
      toast.error("Failed to remove");
      return;
    }
    refresh();
  };

  const move = async (slotId: string, index: number, dir: -1 | 1) => {
    const list = assignedAssets[slotId] || [];
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const a = list[index];
    const b = list[target];
    await Promise.all([
      (supabase as any).from("ad_placements").update({ sort_order: target }).eq("id", a.placement_id),
      (supabase as any).from("ad_placements").update({ sort_order: index }).eq("id", b.placement_id),
    ]);
    refresh();
  };

  if (!eventId) {
    return (
      <div className="text-sm text-muted-foreground p-4 border rounded-md">
        Save the event first to manage banner placements.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Banner Placements</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose which banner assets appear in each placement slot for this event.
          Assets are uploaded in <strong>Advertising → Assets</strong>. Leave a slot empty if you
          don't want a banner there.
        </p>
      </div>

      {updateEvent && (
        <label className="flex items-start gap-3 rounded-lg border p-4 bg-muted/40 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={!!event?.banners_not_needed}
            onChange={(e) => updateEvent({ banners_not_needed: e.target.checked })}
          />
          <span className="text-sm">
            <span className="font-semibold block">This event doesn't need banners</span>
            <span className="text-muted-foreground">
              Check this to mark the Banners step complete without assigning any banner assets.
            </span>
          </span>
        </label>
      )}


      <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 p-4 text-sm space-y-2">
        <p className="font-semibold text-blue-900 dark:text-blue-200">How to load a banner asset</p>
        <ol className="list-decimal pl-5 space-y-1 text-blue-900/90 dark:text-blue-200/90">
          <li>Go to <strong>Advertising → Assets</strong> in the admin sidebar.</li>
          <li>Click <strong>+ New Asset</strong>, upload the image, set advertiser name, optional click URL, and start/end dates.</li>
          <li>Return to this Banners step and click <strong>+ Add Asset</strong> on the slot below to assign it.</li>
          <li>Banners render full-image (contain) — use the recommended dimensions below so nothing gets letterboxed.</li>
        </ol>
        <Link
          to="/admin-dashboard/advertising/assets"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
        >
          Open Advertising → Assets
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="space-y-4">
        {SLOTS.map((slot) => {
          const items = assignedAssets[slot.id] || [];
          return (
            <div key={slot.id} className="border rounded-lg p-4 bg-card">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold">{slot.label}</h3>
                  <p className="text-xs text-muted-foreground">{slot.description}</p>
                  <p className="text-xs mt-1 font-medium text-foreground/80">
                    Recommended: {slot.recommendedSize}
                  </p>
                  {slot.email && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      Active once Email Center ships
                    </span>
                  )}
                </div>
                <Button size="sm" onClick={() => setPickerSlot(slot.id)}>
                  + Add Asset
                </Button>
              </div>

              {items.length > 0 ? (
                <ul className="space-y-2">
                  {items.map((asset, idx) => (
                    <li
                      key={asset.placement_id}
                      className="flex items-center gap-3 p-2 border rounded-md bg-background"
                    >
                      <div className="flex flex-col">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => move(slot.id, idx, -1)}
                          className="p-0.5 disabled:opacity-30"
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          disabled={idx === items.length - 1}
                          onClick={() => move(slot.id, idx, 1)}
                          className="p-0.5 disabled:opacity-30"
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </div>
                      {asset.image_url ? (
                        <img
                          src={asset.image_url}
                          alt={asset.alt_text || asset.name}
                          className="h-12 w-20 object-cover rounded border"
                        />
                      ) : (
                        <div className="h-12 w-20 rounded border bg-muted" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{asset.name}</div>
                        {asset.advertiser_name && (
                          <div className="text-xs text-muted-foreground truncate">
                            {asset.advertiser_name}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAsset(asset.placement_id)}
                        className="text-muted-foreground hover:text-destructive p-1"
                        aria-label="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No assets assigned to this slot.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <AssetPickerModal
        open={!!pickerSlot}
        onOpenChange={(o) => !o && setPickerSlot(null)}
        slot={SLOTS.find((s) => s.id === pickerSlot) || null}
        onSelect={(assetId) => pickerSlot && addAsset(pickerSlot, assetId)}
      />
    </div>
  );
}

function AssetPickerModal({
  open,
  onOpenChange,
  slot,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  slot: { id: string; label: string } | null;
  onSelect: (assetId: string) => void;
}) {
  const [search, setSearch] = useState("");

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["ad-assets-picker", search],
    enabled: open,
    queryFn: async () => {
      let q: any = (supabase as any)
        .from("ad_assets")
        .select("id, name, advertiser_name, image_url, alt_text, start_date, end_date")
        .eq("is_active", true)
        .order("name", { ascending: true });
      if (search) q = q.or(`name.ilike.%${search}%,advertiser_name.ilike.%${search}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Banner — {slot?.label}</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or advertiser…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : assets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No assets in the library yet. Upload assets at Advertising → Assets.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {assets.map((a: any) => (
              <div key={a.id} className="border rounded-md overflow-hidden bg-card flex flex-col">
                <div className="aspect-video bg-muted">
                  {a.image_url && (
                    <img
                      src={a.image_url}
                      alt={a.alt_text || a.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <div className="font-medium text-sm truncate">{a.name}</div>
                  {a.advertiser_name && (
                    <div className="text-xs text-muted-foreground truncate">{a.advertiser_name}</div>
                  )}
                  {(a.start_date || a.end_date) && (
                    <div className="text-xs text-muted-foreground">
                      {a.start_date || "—"} → {a.end_date || "—"}
                    </div>
                  )}
                  <Button size="sm" className="mt-2" onClick={() => onSelect(a.id)}>
                    Select
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
